import {
    ApplicationRef,
    ComponentRef,
    Directive,
    ElementRef,
    EnvironmentInjector,
    HostListener,
    OnDestroy,
    createComponent,
    inject,
    input,
} from "@angular/core";
import { WkOverflowTooltipComponent } from "./overflow-tooltip.component";

let nextId = 0;

@Directive({
    selector: "[wkOverflowTooltip]",
    exportAs: "wkOverflowTooltip",
})
export class WkOverflowTooltipDirective implements OnDestroy {
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    private readonly appRef = inject(ApplicationRef);
    private readonly environmentInjector = inject(EnvironmentInjector);

    public readonly wkOverflowTooltip = input<string>("");
    public readonly wkOverflowTooltipTarget = input<string>("");

    private tooltipRef?: ComponentRef<WkOverflowTooltipComponent>;
    private hovering = false;

    @HostListener("mouseenter")
    protected onMouseEnter(): void {
        this.hovering = true;

        const target = this.getTarget();
        if (!target || !this.isClipped(target)) {
            this.destroyTooltip();
            return;
        }

        target.id ||= `wk-overflow-tooltip-target-${nextId++}`;
        const text = this.wkOverflowTooltip() || (target.textContent ?? "").trim();
        if (!text) return;

        if (this.tooltipRef) {
            this.tooltipRef.setInput("targetSelector", `#${target.id}`);
            this.tooltipRef.setInput("text", text);
            return;
        }

        this.createTooltip(target, text);
    }

    @HostListener("mouseleave")
    protected onMouseLeave(): void {
        this.hovering = false;
    }

    ngOnDestroy(): void {
        this.destroyTooltip();
    }

    private getTarget(): HTMLElement | null {
        const selector = this.wkOverflowTooltipTarget();
        return selector ? this.host.querySelector<HTMLElement>(selector) : this.host;
    }

    private isClipped(target: HTMLElement): boolean {
        if (target.scrollWidth > target.clientWidth || target.scrollHeight > target.clientHeight) return true;

        if (target === this.host) return false;
        const style = getComputedStyle(this.host);
        const hostRect = this.host.getBoundingClientRect();
        const contentRight = hostRect.right - parseFloat(style.paddingRight) - parseFloat(style.borderRightWidth);
        return target.getBoundingClientRect().right > contentRight + 1;
    }

    private createTooltip(target: HTMLElement, text: string): void {
        const ref = createComponent(WkOverflowTooltipComponent, { environmentInjector: this.environmentInjector });
        ref.setInput("targetSelector", `#${target.id}`);
        ref.setInput("text", text);

        this.appRef.attachView(ref.hostView);
        document.body.appendChild(ref.location.nativeElement);
        ref.changeDetectorRef.detectChanges();
        this.tooltipRef = ref;

        setTimeout(() => {
            if (this.hovering && this.tooltipRef === ref) target.dispatchEvent(new MouseEvent("mouseover", { bubbles: true }));
        });
    }

    private destroyTooltip(): void {
        this.tooltipRef?.destroy();
        this.tooltipRef = undefined;
    }
}
